import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Building, 
  MapPin, 
  Phone,
  Mail,
  CreditCard,
  FileText,
  Package,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Input, Select } from '@/components/Input';
import { Card, CardContent } from '@/components/Card';
import { Tabs, TabItem } from '@/components/Tabs';
import { ProviderProductsList } from '@/components/ProviderProductsList';
import { providerService } from '../../services/providerService';
import { ProviderFormData, BRAZILIAN_STATES } from '../../types/provider';
import { useCepSearch } from '../../hooks/useCepSearch';
import { ProviderPaymentTermCard } from './ProviderPaymentTermCard';

/**
 * Formulário de fornecedor com abas para organização das informações
 */
export const ProviderFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('company');
  
  // Hook para busca de CEP
  const { searchCep, isLoading: searchingCep, error: cepError } = useCepSearch();
  
  // Dados do formulário
  const [formData, setFormData] = useState<ProviderFormData>({
    document: '',
    legal_name: '',
    trade_name: '',
    pix_key: '',
    state_registration_tax_id: '',
    non_contributor_state_registration_tax_id: false,
    person_type: 2,
    addr_street: '',
    addr_num: '',
    addr_compl: '',
    addr_district: '',
    addr_city: '',
    addr_state: '',
    addr_zip: '',
    addr_country: 'Brasil',
    phone: '',
    email: ''
  });

  // Validação do formulário
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Carrega fornecedor para edição
  useEffect(() => {
    if (isEditing && id) {
      loadProvider(id);
    }
  }, [id, isEditing]);

  const loadProvider = async (providerId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const provider = await providerService.getProviderById(parseInt(providerId));
      
      setFormData({
        document: provider.document || '',  
        legal_name: provider.legal_name || '',
        trade_name: provider.trade_name || '',
        pix_key: provider.pix_key || '',
        state_registration_tax_id: provider.state_registration_tax_id || '',
        non_contributor_state_registration_tax_id: provider.non_contributor_state_registration_tax_id || false,
        person_type: provider.person_type || 2,
        addr_street: provider.addr_street || '',
        addr_num: provider.addr_num || '',
        addr_compl: provider.addr_compl || '', 
        addr_district: provider.addr_district || '',
        addr_city: provider.addr_city || '',
        addr_state: provider.addr_state || '',
        addr_zip: provider.addr_zip || '',
        addr_country: provider.addr_country || 'Brasil',
        phone: provider.phone || '',
        email: provider.email || ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar fornecedor');
    } finally {
      setLoading(false);
    }
  };

  // Atualiza campo do formulário
  const updateField = <K extends keyof ProviderFormData>(field: K, value: ProviderFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field as string]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as string];
        return newErrors;
      });
    }
  };

  // Handler especializado para CEP com busca automática
  const handleCepChange = async (value: string) => {
    updateField('addr_zip', value);
    
    if (errors.addr_zip) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.addr_zip;
        return newErrors;
      });
    }
    
    const cleanCep = value.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      const result = await searchCep(cleanCep);
      
      if (result) {
        setFormData(prev => ({
          ...prev,
          addr_street: result.logradouro || prev.addr_street,
          addr_district: result.bairro || prev.addr_district,
          addr_city: result.localidade || prev.addr_city,
          addr_state: result.uf || prev.addr_state,
          addr_country: 'Brasil'
        }));
      }
    }
  };
  
  // Sincroniza erros de CEP
  useEffect(() => {
    if (cepError && formData.addr_zip) {
      setErrors(prev => ({ ...prev, addr_zip: cepError }));
    }
  }, [cepError, formData.addr_zip]);

  // Validação do formulário
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.document) {
      newErrors.document = 'CNPJ/CPF é obrigatório';
    }

    if (!formData.legal_name) {
      newErrors.legal_name = 'Razão social é obrigatória';
    }

    if (formData.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (formData.addr_zip && formData.addr_zip.replace(/\D/g, '').length !== 8) {
      newErrors.addr_zip = 'CEP deve ter 8 dígitos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submeter formulário
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Remove formatação dos campos antes de enviar para a API
      const payload = {
        ...formData,
        document: formData.document.replace(/\D/g, ''),
        addr_zip: formData.addr_zip ? formData.addr_zip.replace(/\D/g, '') : formData.addr_zip,
        phone: formData.phone ? formData.phone.replace(/\D/g, '') : formData.phone,
        state_registration_tax_id: formData.state_registration_tax_id
          ? formData.state_registration_tax_id.replace(/\D/g, '')
          : formData.state_registration_tax_id,
      };

      if (isEditing && id) {
        await providerService.updateProvider(parseInt(id), payload);
      } else {
        await providerService.createProvider(payload);
      }

      navigate('/providers');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar fornecedor');
    } finally {
      setSaving(false);
    }
  };

  // Definir abas
  const tabs: TabItem[] = [
    {
      id: 'company',
      label: 'Empresa',
      icon: <Building size={16} />,
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Tipo de Pessoa"
              value={formData.person_type === 1 ? 'Pessoa Física' : 'Pessoa Jurídica'}
              disabled
              startIcon={<FileText size={20} />}
            />
            
            <Input
              label={formData.person_type === 1 ? 'CPF' : 'CNPJ'}
              placeholder={formData.person_type === 1 ? '000.000.000-00' : '00.000.000/0000-00'}
              value={formData.document}
              onChange={(e) => updateField('document', e.target.value)}
              error={errors.document}
              required
              startIcon={<CreditCard size={20} />}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Razão Social"
              placeholder="Nome da empresa conforme CNPJ"
              value={formData.legal_name}
              onChange={(e) => updateField('legal_name', e.target.value)}
              error={errors.legal_name}
              required
              startIcon={<Building size={20} />}
            />
            
            <Input
              label="Nome Fantasia"
              placeholder="Nome comercial da empresa"
              value={formData.trade_name}
              onChange={(e) => updateField('trade_name', e.target.value)}
              error={errors.trade_name}
              startIcon={<Building size={20} />}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Telefone"
              placeholder="(00) 00000-0000"
              value={formData.phone}
              onChange={(e) => updateField('phone', e.target.value)}
              error={errors.phone}
              startIcon={<Phone size={20} />}
            />
            
            <Input
              label="Email"
              type="email"
              placeholder="contato@empresa.com"
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              error={errors.email}
              startIcon={<Mail size={20} />}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Inscrição Estadual"
              placeholder="000.000.000.000"
              value={formData.state_registration_tax_id}
              onChange={(e) => updateField('state_registration_tax_id', e.target.value)}
              error={errors.state_registration_tax_id}
              startIcon={<FileText size={20} />}
            />
            
            <div className="flex items-center space-x-2 mt-8">
              <input
                type="checkbox"
                id="non_contributor"
                checked={formData.non_contributor_state_registration_tax_id}
                onChange={(e) => updateField('non_contributor_state_registration_tax_id', e.target.checked)}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="non_contributor" className="text-sm text-slate-700">
                Isento de Inscrição Estadual
              </label>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'address',
      label: 'Endereço',
      icon: <MapPin size={16} />,
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input
              label="CEP"
              placeholder="00000-000"
              value={formData.addr_zip}
              onChange={(e) => handleCepChange(e.target.value)}
              error={errors.addr_zip}
              maxLength={9}
              startIcon={<MapPin size={20} />}
              disabled={searchingCep}
              helperText={searchingCep ? 'Buscando endereço...' : 'Digite o CEP para preencher automaticamente'}
            />
            
            <div className="md:col-span-2">
              <Input
                label="Endereço"
                placeholder="Nome da rua, avenida, etc."
                value={formData.addr_street}
                onChange={(e) => updateField('addr_street', e.target.value)}
                error={errors.addr_street}
                startIcon={<MapPin size={20} />}
              />
            </div>
            
            <Input
              label="Número"
              placeholder="123"
              value={formData.addr_num}
              onChange={(e) => updateField('addr_num', e.target.value)}
              error={errors.addr_num}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Complemento"
              placeholder="Apartamento, sala, etc."
              value={formData.addr_compl}
              onChange={(e) => updateField('addr_compl', e.target.value)}
              error={errors.addr_compl}
            />
            
            <Input
              label="Bairro"
              placeholder="Nome do bairro"
              value={formData.addr_district}
              onChange={(e) => updateField('addr_district', e.target.value)}
              error={errors.addr_district}
              startIcon={<MapPin size={20} />}
            />
            
            <Input
              label="Cidade"
              placeholder="Nome da cidade"
              value={formData.addr_city}
              onChange={(e) => updateField('addr_city', e.target.value)}
              error={errors.addr_city}
              startIcon={<MapPin size={20} />}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Estado"
              value={formData.addr_state}
              onChange={(e) => updateField('addr_state', e.target.value)}
              error={errors.addr_state}
            >
              <option value="">Selecione o estado</option>
              {BRAZILIAN_STATES.map(state => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </Select>
            
            <Input
              label="País"
              value={formData.addr_country}
              onChange={(e) => updateField('addr_country', e.target.value)}
              error={errors.addr_country}
              startIcon={<MapPin size={20} />}
            />
          </div>
        </div>
      )
    },
    {
      id: 'payment',
      label: 'Pagamento',
      icon: <CreditCard size={16} />,
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Chave PIX"
              placeholder="CPF, CNPJ, celular, email ou chave aleatória"
              value={formData.pix_key}
              onChange={(e) => updateField('pix_key', e.target.value)}
              error={errors.pix_key}
              startIcon={<CreditCard size={20} />}
            />
          </div>

          <hr className="border-slate-200" />
          {/* Condição de pagamento: gravada à parte (rota própria), não no Salvar do formulário. */}
          {isEditing && id ? (
            <ProviderPaymentTermCard providerId={parseInt(id)} />
          ) : (
            <p className="text-sm text-slate-400">
              Salve o fornecedor para cadastrar a condição de pagamento (prazos e forma de pagamento acordados).
            </p>
          )}
        </div>
      )
    },
    {
      id: 'products',
      label: 'Produtos',
      icon: <Package size={16} />,
      disabled: !isEditing,
      content: isEditing && id ? (
        <ProviderProductsList 
          providerId={parseInt(id)} 
          onUpdate={() => {
            // Callback para atualizar dados se necessário
          }}
        />
      ) : (
        <div className="text-center py-8">
          <Package className="mx-auto h-12 w-12 text-slate-400" />
          <h3 className="mt-2 text-sm font-medium text-slate-900">Produtos do Fornecedor</h3>
          <p className="mt-1 text-sm text-slate-500">
            Salve o fornecedor primeiro para gerenciar os produtos.
          </p>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/providers')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        <div className="h-96 bg-slate-200 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/providers')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-h1">
              {isEditing ? 'Editar Fornecedor' : 'Novo Fornecedor'}
            </h1>
            <p className="text-slate-600">
              {isEditing 
                ? 'Altere as informações do fornecedor'
                : 'Preencha os dados para cadastrar um novo fornecedor'
              }
            </p>
          </div>
        </div>

        {/* Ações */}
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleSubmit}
            disabled={saving}
          >
            <Save size={20} className="mr-2" />
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>

      {/* Mensagem de Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="flex items-center space-x-2">
              <AlertCircle className="text-red-500" size={20} />
              <span className="text-red-700">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Formulário com Abas */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent>
            <Tabs
              tabs={tabs}
              defaultTab={activeTab}
              onTabChange={setActiveTab}
            />
          </CardContent>
        </Card>
      </form>
    </div>
  );
};